import { useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Clock, Gem, Gift, Lock, Ticket } from "lucide-react";
import { PublicLayout } from "../../components/PublicLayout";
import { StatusTag } from "../../components/StatusTag";
import { api } from "../../lib/api";
import { isLocalDevFrontend } from "../../lib/env";

interface GraduateWeekTask {
  task_code: "checkin" | "single1000" | "total5000";
  title: string;
  reward_text: string;
  status: "completed" | "available" | "locked" | "not_eligible";
  completed_at?: string | null;
  deposit_id?: number | null;
  deposit_amount?: number | null;
  cumulative_amount?: number | null;
}

interface GraduateWeekStatus {
  is_graduated: boolean;
  is_open: boolean;
  is_expired: boolean;
  message?: string;
  graduation_at?: string | null;
  week_start_at?: string | null;
  week_end_at?: string | null;
  remaining_text?: string;
  deposits?: {
    single_1000_deposit_id?: number | null;
    single_1000_amount?: number | null;
    cumulative_amount?: number;
  };
  tasks: GraduateWeekTask[];
}

const v2Tasks = [
  ["畢業首週累積充值滿 10,000", "輪盤資格 1 次", "進階任務"],
  ["畢業首週累積充值滿 30,000", "高額加碼券資格", "進階任務"],
  ["畢業首週累積充值滿 50,000", "會長關懷標記 / 尊享紅包券", "進階任務"]
] as const;

export function GraduateWeekPage() {
  const isLocalDev = isLocalDevFrontend();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const savedAccount = params.get("member_account") ?? params.get("account") ?? localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? (isLocalDev ? "test006" : "");
  const memberAccount = savedAccount;
  const [status, setStatus] = useState<GraduateWeekStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const taskMap = useMemo(() => new Map(status?.tasks.map((task) => [task.task_code, task]) ?? []), [status]);

  async function load(account = memberAccount) {
    if (!account.trim()) return;
    setLoading(true);
    try {
      setStatus(await api<GraduateWeekStatus>(`/api/public/graduate-week?member_account=${encodeURIComponent(account.trim())}`));
      setMessage("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "畢業首週資料讀取失敗");
    } finally {
      setLoading(false);
    }
  }

  async function runAction(action: "checkin" | "check_deposit_rewards") {
    setLoading(true);
    try {
      const result = await api<{ message?: string } | Record<string, unknown>>("/api/public/graduate-week", {
        method: "POST",
        body: JSON.stringify({ member_account: memberAccount.trim(), action })
      });
      setMessage(action === "checkin" ? getResultMessage(result, "畢業打卡已處理") : "已檢查畢業首週充值任務");
      await load(memberAccount);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "操作失敗");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(savedAccount).catch(() => undefined);
  }, []);

  if (!memberAccount.trim()) {
    return (
      <PublicLayout title="畢業首週啟航禮" subtitle="畢業後 7 天限時任務">
        <section className="action-panel">
          <h2>請從活動入口進入</h2>
          <p>請先完成會員綁定，或從龍來喬大廳進入畢業首週啟航禮。</p>
          <a className="primary-cta" href="/">返回龍來喬大廳</a>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout title="畢業首週啟航禮" subtitle="畢業後 7 天限時任務">
      <section className="limited-event-panel graduate-week-hero">
        <div className="graduate-week-hero-icon"><CalendarDays size={34} /></div>
        <div>
          <StatusTag label={status?.is_open && !status?.is_expired ? "已開啟" : status?.is_expired ? "已結束" : "尚未開放"} tone={status?.is_open && !status?.is_expired ? "active" : "locked"} />
          <h2>畢業首週啟航禮</h2>
          <p>新人七關完成後，畢業首週可完成打卡與充值任務，取得寶石碎片與啟航紅包券。畢業首週為限時加碼活動，完成越多任務可領越多獎勵。</p>
          <strong>{status?.remaining_text || status?.message || "完成新人七關後開啟"}</strong>
        </div>
      </section>

      {message ? <p className="form-message">{message}</p> : null}

      <p className="graduate-week-rule-note">
        單筆充值 5,000 可同時完成「單筆充值滿 1,000」與「累積充值滿 5,000」任務，各任務限領一次。
      </p>

      <section className="graduate-week-task-list">
        <GraduateTaskCard
          icon={<Gem size={22} />}
          task={taskMap.get("checkin")}
          fallbackTitle="畢業打卡"
          fallbackReward="寶石碎片 +1"
          actionLabel="完成畢業打卡"
          onAction={() => runAction("checkin")}
          loading={loading}
          guideHref="/group-checkin"
          guideLabel="前往 LINE 群打卡"
        />
        <GraduateTaskCard
          icon={<Ticket size={22} />}
          task={taskMap.get("single1000")}
          fallbackTitle="畢業首週單筆充值滿 1,000"
          fallbackReward="畢業啟航紅包券 1 張"
          actionLabel="檢查充值任務"
          onAction={() => runAction("check_deposit_rewards")}
          loading={loading}
        />
        <GraduateTaskCard
          icon={<Gift size={22} />}
          task={taskMap.get("total5000")}
          fallbackTitle="畢業首週累積充值滿 5,000"
          fallbackReward="高級啟航紅包券 1 張"
          actionLabel="檢查充值任務"
          onAction={() => runAction("check_deposit_rewards")}
          loading={loading}
        />
      </section>

      <section className="tier-list graduate-v2-list">
        {v2Tasks.map(([title, reward, label]) => (
          <article className="tier-card is-disabled" key={title}>
            <StatusTag label={label} tone="soon" />
            <h3>{title}</h3>
            <p>{reward}</p>
          </article>
        ))}
      </section>
    </PublicLayout>
  );
}

function GraduateTaskCard({
  icon,
  task,
  fallbackTitle,
  fallbackReward,
  actionLabel,
  onAction,
  loading,
  guideHref,
  guideLabel
}: {
  icon: React.ReactNode;
  task?: GraduateWeekTask;
  fallbackTitle: string;
  fallbackReward: string;
  actionLabel: string;
  onAction: () => void;
  loading: boolean;
  guideHref?: string;
  guideLabel?: string;
}) {
  const status = task?.status ?? "locked";
  const done = status === "completed";
  const available = status === "available";

  return (
    <article className={`graduate-week-task-card ${done ? "is-done" : ""}`}>
      <div className="graduate-week-task-icon">{done ? <CheckCircle2 size={24} /> : available ? icon : <Lock size={22} />}</div>
      <div className="graduate-week-task-body">
        <StatusTag label={statusLabel(status)} tone={statusTone(status)} />
        <h3>{task?.title ?? fallbackTitle}</h3>
        <p>{task?.reward_text ?? fallbackReward}</p>
        {task?.completed_at ? <small>完成時間：{formatTime(task.completed_at)}</small> : null}
        {task?.deposit_amount ? <small>符合存款：{Number(task.deposit_amount).toLocaleString("zh-TW")}</small> : null}
        {typeof task?.cumulative_amount === "number" ? <small>目前累積：{Number(task.cumulative_amount).toLocaleString("zh-TW")}</small> : null}
      </div>
      <div className="task-action-stack">
        {guideHref ? <a className="secondary-cta task-guide-link" href={guideHref}>{guideLabel ?? "查看方式"}</a> : null}
        <button className="primary-cta" type="button" disabled={!available || loading} onClick={onAction}>
          {done ? "已完成" : available ? actionLabel : status === "not_eligible" ? "未達成" : "尚未開放"}
        </button>
      </div>
    </article>
  );
}

function statusLabel(status: GraduateWeekTask["status"]) {
  if (status === "completed") return "已完成";
  if (status === "available") return "可完成";
  if (status === "not_eligible") return "未達成";
  return "尚未開放";
}

function statusTone(status: GraduateWeekTask["status"]) {
  if (status === "completed") return "done";
  if (status === "available") return "claim";
  if (status === "not_eligible") return "pending";
  return "locked";
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-TW", { hour12: false });
}

function getResultMessage(result: unknown, fallback: string) {
  if (result && typeof result === "object" && "message" in result) {
    const message = (result as { message?: string }).message;
    if (message) return message;
  }
  return fallback;
}
